import React, { useState, useMemo } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Loader2 } from 'lucide-react';
import { useExpenseCategories, useCreateExpenseCategory, useUpdateExpenseCategory } from '@/hooks/finance/useExpenseCategories';
import { toast } from 'sonner';
import type { ExpenseCategory, CategoryType, CashFlowType } from '@/types/database/expense-categories';
import { BUSINESS_UNITS, BusinessUnit } from '@/constants/businessUnits';
import { ExpenseCategoryGroupView } from '@/components/finance/expense-categories/ExpenseCategoryGroupView';
import { QuickAddCostPosition } from '@/components/finance/expense-categories/QuickAddCostPosition';
import { DepartmentCategoryAssignment } from '@/components/finance/expense-categories/DepartmentCategoryAssignment';
import { useAuth } from '@/contexts/AuthContext';

const ExpenseCategoriesSettingsPage = () => {
  const { data: categories, isLoading } = useExpenseCategories();
  const createCategory = useCreateExpenseCategory();
  const updateCategory = useUpdateExpenseCategory();
  const { user: profile } = useAuth();
  
  const isAdmin = profile?.role === 'admin' || profile?.role === 'finance';
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    parent_id: '',
    gl_account_code: '',
    is_active: true,
    category_type: 'cost_position' as CategoryType,
    cash_flow_type: '' as CashFlowType | '',
    default_business_unit: '' as BusinessUnit | '',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      parent_id: '',
      gl_account_code: '',
      is_active: true,
      category_type: 'cost_position',
      cash_flow_type: '',
      default_business_unit: '',
    });
    setEditingCategory(null);
  };

  const openEditDialog = (category: ExpenseCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      code: category.code,
      description: category.description || '',
      parent_id: category.parent_id || '',
      gl_account_code: category.gl_account_code || '',
      is_active: category.is_active ?? true,
      category_type: category.category_type || 'cost_position',
      cash_flow_type: category.cash_flow_type || '',
      default_business_unit: ((category as any).default_business_unit || '') as BusinessUnit | '',
    });
    setIsDialogOpen(true);
  };

  // Get available parent categories based on the selected category type
  const availableParents = useMemo(() => {
    if (!categories) return [];
    
    switch (formData.category_type) {
      case 'expense_category':
        // Expense categories cannot have a parent
        return [];
      case 'cost_group':
        // Cost groups must have an expense_category as parent
        return categories.filter(c => c.category_type === 'expense_category');
      case 'cost_position':
        // Cost positions must have a cost_group as parent
        return categories.filter(c => c.category_type === 'cost_group');
      default:
        return [];
    }
  }, [categories, formData.category_type]);

  // Determine if is_recordable based on category type
  const isRecordable = formData.category_type === 'cost_position';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      code: formData.code.toUpperCase(),
      description: formData.description || null,
      parent_id: formData.parent_id || null,
      gl_account_code: formData.gl_account_code || null,
      is_active: formData.is_active,
      category_type: formData.category_type,
      is_recordable: isRecordable,
      default_business_unit: isRecordable ? (formData.default_business_unit || null) : null,
      cash_flow_type: formData.category_type === 'expense_category' 
        ? (formData.cash_flow_type || null) 
        : (() => {
            // Inherit cash_flow_type from parent hierarchy
            const parent = categories?.find(c => c.id === formData.parent_id);
            if (parent?.cash_flow_type) return parent.cash_flow_type;
            // For cost_position, look up grandparent via cost_group
            if (formData.category_type === 'cost_position' && parent?.parent_id) {
              const grandparent = categories?.find(c => c.id === parent.parent_id);
              return grandparent?.cash_flow_type || null;
            }
            return null;
          })(),
    };

    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({ id: editingCategory.id, ...data });
        toast.success('Category updated successfully');
      } else {
        await createCategory.mutateAsync(data);
        toast.success('Category created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  // Reset parent_id when category type changes
  const handleCategoryTypeChange = (type: CategoryType) => {
    setFormData(prev => ({
      ...prev,
      category_type: type,
      parent_id: '', // Reset parent when type changes
      cash_flow_type: type === 'expense_category' ? prev.cash_flow_type : '',
    }));
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Expense Categories</h1>
            <p className="text-muted-foreground">Manage categories for expense classification</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
                <DialogDescription>
                  {editingCategory ? 'Update category details' : 'Create a new expense category'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="category_type">Category Level</Label>
                  <Select
                    value={formData.category_type}
                    onValueChange={(value: CategoryType) => handleCategoryTypeChange(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense_category">
                        Expense Category (Level 1 - e.g., COGS, SG&A)
                      </SelectItem>
                      <SelectItem value="cost_group">
                        Cost Group (Level 2 - e.g., Delivery Service)
                      </SelectItem>
                      <SelectItem value="cost_position">
                        Cost Position (Level 3 - e.g., Fuel, recordable)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.category_type === 'expense_category' && (
                  <div className="space-y-2">
                    <Label htmlFor="cash_flow_type">Cash Flow Type</Label>
                    <Select
                      value={formData.cash_flow_type || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, cash_flow_type: value === 'none' ? '' : value as CashFlowType })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select cash flow type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        <SelectItem value="operating">Operating Activities</SelectItem>
                        <SelectItem value="investing">Investing Activities</SelectItem>
                        <SelectItem value="financing">Financing Activities</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Category Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Fuel"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="code">Code</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., COGS-DEL-FUEL"
                      required
                    />
                  </div>
                </div>

                {availableParents.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="parent">
                      Parent {formData.category_type === 'cost_group' ? 'Expense Category' : 'Cost Group'} *
                    </Label>
                    <Select
                      value={formData.parent_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select parent" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" disabled>Select a parent</SelectItem>
                        {availableParents.map((cat) => (
                          <SelectItem 
                            key={cat.id} 
                            value={cat.id}
                            disabled={editingCategory?.id === cat.id}
                          >
                            {cat.name} ({cat.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this category"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gl_account_code">GL Account Code (Optional)</Label>
                  <Input
                    id="gl_account_code"
                    value={formData.gl_account_code}
                    onChange={(e) => setFormData({ ...formData, gl_account_code: e.target.value })}
                    placeholder="e.g., 6100"
                  />
                </div>

                {isRecordable && (
                  <div className="space-y-2">
                    <Label htmlFor="default_business_unit">Default Business Unit (Optional)</Label>
                    <Select
                      value={formData.default_business_unit || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, default_business_unit: value === 'none' ? '' : value as BusinessUnit })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="None — choose per expense" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None — choose per expense</SelectItem>
                        {BUSINESS_UNITS.map((bu) => (
                          <SelectItem key={bu.value} value={bu.value}>{bu.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">When set, picking this cost position in an expense form auto-fills the BU (still overridable).</p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label htmlFor="is_active">Active</Label>
                  </div>
                  {isRecordable && (
                    <span className="text-sm text-green-600 font-medium">
                      ✓ This category can be used when recording expenses
                    </span>
                  )}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createCategory.isPending || updateCategory.isPending}>
                    {editingCategory ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="categories" className="w-full">
          <TabsList>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="quick-add">Quick Add Cost Position</TabsTrigger>
            {isAdmin && <TabsTrigger value="department-assignments">Department Assignments</TabsTrigger>}
          </TabsList>
          <TabsContent value="categories" className="mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ExpenseCategoryGroupView 
                categories={categories || []} 
                onEdit={openEditDialog}
                isAdmin={isAdmin}
              />
            )}
          </TabsContent>
          <TabsContent value="quick-add" className="mt-4">
            <QuickAddCostPosition />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="department-assignments" className="mt-4">
              <DepartmentCategoryAssignment />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default ExpenseCategoriesSettingsPage;
